import React, { useState } from 'react';
import { Mail, Check, Link } from 'lucide-react';

const AddEmailAccountModal = ({
  isOpen,
  onClose,
  newEmailName,
  setNewEmailName,
  addEmailError,
  setAddEmailError,
  userId
}) => {
  const [connectionMethod] = useState('nylas');
  
  if (!isOpen) return null;

  const handleConnect = () => {
    if (!newEmailName.trim()) {
      setAddEmailError('Please enter an account name');
      return;
    }
    
    // Prevent connection if user is not logged in
    if (!userId || userId === 'null' || userId === 'undefined') {
      setAddEmailError('You must be logged in to connect an email account. Please refresh the page and try again.');
      return;
    }
    
    const accountName = encodeURIComponent(newEmailName.trim());
    const redirectHost = encodeURIComponent(window.location.origin);
    
    // Use Nylas for all email providers (Gmail, Outlook, Yahoo, IMAP)
    window.location.href = `${window.location.origin}/api/nylas/auth/connect?user_id=${userId}&account_name=${accountName}&redirect_to=communications-re&redirect_host=${redirectHost}`;
  };

  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" 
      onClick={onClose}
    >
      <div 
        className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-700">
          <h3 className="text-xl font-bold text-white mb-1">Connect Email Account</h3>
          <p className="text-sm text-gray-400">Choose your preferred connection method</p>
        </div>
        
        <div className="p-6 space-y-4">
          {addEmailError && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {addEmailError}
            </div>
          )}
          
          {/* Account Name */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Account Name</label>
            <input
              type="text"
              value={newEmailName}
              onChange={(e) => setNewEmailName(e.target.value)}
              placeholder="e.g., Work, Personal, Business"
              className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
              data-testid="new-email-name-input"
            />
            <p className="mt-1 text-xs text-gray-500">This name will appear as the tab label</p>
          </div>
          
          {/* Connection Method Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">Connection Method</label>
            <div className="space-y-2">
              {/* Nylas Option - All Providers including Gmail */}
              <button
                type="button"
                onClick={() => setConnectionMethod('nylas')}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                  connectionMethod === 'nylas'
                    ? 'border-purple-500 bg-purple-500/10'
                    : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    connectionMethod === 'nylas' ? 'border-purple-500 bg-purple-500' : 'border-gray-500'
                  }`}>
                    {connectionMethod === 'nylas' && <Check size={12} className="text-white" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Mail className="w-5 h-5 text-purple-400" />
                      <span className="font-semibold text-white">Connect Email Account</span>
                      <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">Recommended</span>
                    </div>
                    <p className="text-xs text-gray-400">Connect Gmail, Outlook, Yahoo, or any IMAP email provider.</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <span className="px-2 py-0.5 bg-red-500/20 text-red-300 rounded text-xs">Gmail</span>
                      <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded text-xs">Outlook</span>
                      <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded text-xs">Yahoo</span>
                      <span className="px-2 py-0.5 bg-gray-700 rounded text-xs text-gray-300">IMAP</span>
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </div>
          
          {/* Info Box */}
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Check className="w-4 h-4 text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-purple-400 font-medium">Secure One-Click Connect</p>
                <p className="text-xs text-gray-400 mt-1">
                  Click connect and sign in with your email provider. No passwords stored on our servers.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="p-6 border-t border-gray-700 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConnect}
            className="flex-1 px-4 py-3 text-white rounded-lg transition-all font-medium flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
            data-testid="connect-email-btn"
          >
            <Link className="w-4 h-4" />
            Connect Email
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEmailAccountModal;
